/* eslint-disable @typescript-eslint/no-empty-function */
import { jsPDF } from "jspdf";
import uuid from "@/app/infrastructures/misc/common-library/Uuid";
import {
  convertJsBarcode,
  convertQRCode
} from "@/app/ui/modules/receipt/module";
import { formatDateNormal, formatByCountry, parseByCountry } from "@/app/infrastructures/misc/Utils";
import { CargoV2Data, CargoV2FlightDetail } from "@/domain/entities/CargoV2";

const getDestinationCode = (data: CargoV2Data): string =>
  (data.cargoType === "plane"
    ? data.flightsDetail[data.flightsDetail.length - 1]?.cfAirportDestination
    : data.destinationCityCode) || "";

const renderDestination = (doc: any, data: CargoV2Data) => {
  const destination3LC = getDestinationCode(data);
  
    doc.setFont("Montserrat", "bold");
    let fontSize3LC = 44;
    let textDimensions = doc.getTextDimensions(destination3LC, { maxWidth: undefined });
    let textWidth = textDimensions.w;
    let textHeight = textDimensions.h;
    while (textWidth < 47) {
      fontSize3LC++;
      doc.setFontSize(fontSize3LC);
      textDimensions = doc.getTextDimensions(destination3LC, { maxWidth: undefined });
      if (textDimensions.w > 47.5 || textDimensions.h > 22) {
        break;
      }
      textWidth = textDimensions.w;
      textHeight = textDimensions.h;
    }
    const pad = 1; // padding in mm

    // Rectangle parameters for right alignment
    const rectRight = 102; // right edge
    const textY = 14 + textHeight - 1;
    const rectWidth = textWidth + 2 * pad;
    const rectLeft = rectRight - rectWidth - 0.5;
    const rectHeight = textHeight - 1;
    const rectTop = textY - rectHeight + 2.5;

    // Draw black background rectangle
    doc.setFillColor("#000000");
    doc.rect(rectLeft, rectTop, rectWidth, rectHeight, "F");

    // Draw the 3LC text in white, align "right" inside the black box padding
    doc.setTextColor("#FFFFFF");
    doc.text(
      destination3LC,
      rectRight - pad, // flush right inside the black box
      textY,
      { align: "right" }
    );
};

const labelPlanePdfV2 = async (data: CargoV2Data, logo: string) => {
  const doc: any = new jsPDF("p", "mm", "a6", true);

  const resolveLabelSegments = (data: CargoV2Data): CargoV2FlightDetail[] => {
    if (data.cargoLabelFlightSegments?.length) {
      return data.cargoLabelFlightSegments;
    }

    const seen = new Set<string>();
    return data.flightsDetail.filter((f) => {
      if (!data.isFlightVia) return true;
      if (seen.has(f.cfNumber)) return false;
      seen.add(f.cfNumber);
      return true;
    });
  };

  const visibleFlights = resolveLabelSegments(data);

  const generate = async (
    pieceCount: number,
    qrcode: string,
    newBarcode: string
  ) => {
    logo
      ? doc.addImage(logo, "PNG", 75, 3, 25, 7, uuid(), "FAST", 0)
      : doc.text("", 1, 1);
    doc.addImage(qrcode, "PNG", 1, 1.5, 35, 35, uuid(), "FAST", 0);

    // WEIGHT (KG)
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.setTextColor("#6a6a6a");
    doc.text("WEIGHT (KG)", 35, 6.5);

    // Total Weight ${totalActualCargoGrossWeight}
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(21);
    doc.setTextColor("#000000");
    const totalWeight = formatByCountry(parseByCountry(data.totalActualCargoGrossWeight), { type: "weight", fractionDigits: 1 });
    doc.text(`      /${totalWeight}`, 35, 15);

    // PIECES
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.setTextColor("#6a6a6a");
    doc.text("PIECES", 35, 24.5);

    // Actual Piece ${pieceCount}/${cargoActualPiece}
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(19);
    doc.setTextColor("#000000");
    doc.text(`${pieceCount}/${data.cargoActualPiece}`, 35, 33);

    // DESTINATION
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.setTextColor("#6a6a6a");
    doc.text("DESTINATION", 102, 15, "right");

    renderDestination(doc, data);

    //AWB NUMBER ${cargoNumber}
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(35);
    doc.setTextColor("#000000");
    doc.text(data.cargoNumber, 52.5, 48, { align: "center" });

    // LINE 1
    doc.line(3, 52, 102, 52);

    // ORIGIN
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.setTextColor("#6a6a6a");
    doc.text("ORIGIN", 3, 57);

    // ORIGIN CITY
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(40);
    doc.setTextColor("#000000");
    doc.text(visibleFlights[0]?.cfAirportOrigin, 3, 70);

    // FLIGHT NO ORIGIN
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(30);
    doc.setTextColor("#000000");
    doc.text(visibleFlights.length ? visibleFlights[0]?.cfNumber : "", 53, 64);

    // FLIGHT DATE ORIGIN
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(19);
    doc.setTextColor("#000000");
    doc.text(
      visibleFlights.length
        ? formatDateNormal(
            visibleFlights[0]?.cfEstDepartureTime,
            "DD MMM HH:mm"
          )
        : "",
      53,
      71
    );

    // LINE 2
    doc.line(3, 73, 102, 73);

    // TRANSIT-1
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.setTextColor("#6a6a6a");
    doc.text("TRANSIT-1", 3, 77);

    // TRANSIT-1 CITY
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(40);
    doc.setTextColor("#000000");
    doc.text(
      visibleFlights.length > 1 ? visibleFlights[1]?.cfAirportOrigin : "",
      3,
      89
    );

    // FLIGHT NO TRANSIT-1
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(30);
    doc.setTextColor("#000000");
    doc.text(
      visibleFlights.length > 1 ? visibleFlights[1]?.cfNumber : "",
      53,
      83
    );

    // FLIGHT DATE TRANSIT-1
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(19);
    doc.setTextColor("#000000");
    doc.text(
      visibleFlights.length > 1
        ? formatDateNormal(
            visibleFlights[1]?.cfEstDepartureTime,
            "DD MMM HH:mm"
          )
        : "",
      53,
      90
    );

    // LINE 3
    doc.line(3, 94, 102, 94);

    // TRANSIT-2
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.setTextColor("#6a6a6a");
    doc.text("TRANSIT-2", 3, 98);

    // TRANSIT-2 CITY
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(40);
    doc.setTextColor("#000000");
    doc.text(
      visibleFlights.length > 2 ? visibleFlights[2]?.cfAirportOrigin : "",
      3,
      110
    );

    // FLIGHT NO TRANSIT-2
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(24);
    doc.setTextColor("#000000");
    doc.text(
      visibleFlights.length > 2 ? visibleFlights[2]?.cfNumber : "",
      3,
      118
    );

    // FLIGHT DATE TRANSIT-2
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.setTextColor("#000000");
    doc.text(
      doc.splitTextToSize(
        visibleFlights.length > 2
          ? formatDateNormal(
              visibleFlights[2]?.cfEstDepartureTime,
              "DD MMM HH:mm"
            )
          : "",
        12
      ),
      40,
      114
    );

    // TRANSIT-3
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.setTextColor("#6a6a6a");
    doc.text("TRANSIT-3", 53, 98);

    // TRANSIT-3 CITY
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(40);
    doc.setTextColor("#000000");
    doc.text(
      visibleFlights.length > 3 ? visibleFlights[3]?.cfAirportOrigin : "",
      53,
      110
    );

    // FLIGHT NO TRANSIT-3
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(24);
    doc.setTextColor("#000000");
    doc.text(
      visibleFlights.length > 3 ? visibleFlights[3]?.cfNumber : "",
      53,
      118
    );

    // FLIGHT DATE TRANSIT-2
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(10);
    doc.setTextColor("#000000");
    doc.text(
      doc.splitTextToSize(
        visibleFlights.length > 3
          ? formatDateNormal(
              visibleFlights[3]?.cfEstDepartureTime,
              "DD MMM HH:mm"
            )
          : "",
        12
      ),
      90,
      114
    );

    //BARCODE
    doc.addImage(newBarcode, "PNG", 2.5, 119, 100, 20, uuid(), "FAST", 0);

    // LION CARGO AGENT 3LC
    doc.setFont("Montserrat", "bold");
    doc.setFontSize(19);
    doc.text(`LION PARCEL ${data.originCityCode}`, 53, 144.5, {
      align: "center"
    });
  };

  const transitsOrigin = [];
  const transitFlight = [];
  for (const item of data.cargoLabelFlightSegments) {
    transitsOrigin.push(`${item.cfAirportOrigin}`);
    transitFlight.push(item.cfNumber);
  }
  const transitOriginQr =
    transitsOrigin.length > 1 ? transitsOrigin.slice(1).join(",") : "";
  const transitFlightQr = transitFlight.join(",");
  let noPieces = 0;

  const actualPieces = Math.max(1, data.cargoActualPiece || 0);
  // duplicate label match number of total pieces
  for (let i = 0; i < actualPieces; i++) {
    if (i > 0) {
      doc.addPage("a6", "p");
    }

    noPieces = i + 1;
    const newCombineQRText = `${data.cargoNumber}#${
      data.cargoNumber
    }${noPieces.toString().padStart(5, "0")}#${data.originCityCode}#${
      data.destinationCityCode
    }#${transitOriginQr}#${data.totalActualCargoGrossWeight}#${
      data.cargoActualPiece
    }#${data.nGenCreatedAt || data.createdAt}#${transitFlightQr};`;
    const newQRCode = await convertQRCode(newCombineQRText);
    const newBarcode = await convertJsBarcode(
      `${data.cargoNumber}${noPieces.toString().padStart(5, "0")}`
    );
    generate(noPieces, newQRCode, newBarcode);
  }

  doc.setProperties({
    title: `Cargo Label.pdf`
  });

  doc.autoPrint();

  window.open(doc.output("bloburl"), "_blank");
};

export default labelPlanePdfV2;
